import { FC, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { Box, FileButton, Group, Image, Loader, Paper, Stack, Text } from '@mantine/core';
import { Dropzone, FileRejection } from '@mantine/dropzone';
import { IconCirclePlus, IconPhoto, IconUpload, IconX } from '@tabler/icons-react';
import FileService from '@/services/file-service';
import { MAX_LOGO_BYTES } from '@/PageComponents/Onboarding/onboarding-config';
import { PrimaryButton, SecondaryButton } from '@/PageComponents/Onboarding/components/OnboardingButtons';
import styles from '@/PageComponents/Onboarding/components/LogoSetup.module.css';

// Same restrictions as the job-card wizard's logo upload (JobCardWizardUploadDropZone).
export const ACCEPTED_IMAGE_TYPES = FileService.imageExtensions().map((ext: string) => `image/${ext}`);

export type LogoRejectionReason = 'too_large' | 'not_an_image' | 'unusable';

export const logoRejectionReason = (rejections: FileRejection[]): LogoRejectionReason => {
    const codes = rejections[0]?.errors.map(e => e.code) ?? [];
    if (codes.includes('file-too-large')) return 'too_large';
    if (codes.includes('file-invalid-type')) return 'not_an_image';
    return 'unusable';
};

export const describeLogoRejection = (rejections: FileRejection[]): string => {
    switch (logoRejectionReason(rejections)) {
        case 'too_large': return `Logo must be smaller than ${MAX_LOGO_BYTES / 1024 ** 2}MB.`;
        case 'not_an_image': return 'That file type is not supported. Please choose an image.';
        default: return "We couldn't use that file. Please choose an image.";
    }
};

export interface LogoSetupProps {
    logoUrl: string | null | undefined;
    uploading: boolean;
    disabled: boolean;
    onFileSelected: (file: File) => void;
    onFileRejected: (message: string, reason: LogoRejectionReason) => void;
    /** The user chose "I don't have one": the logo no longer holds up step 2, but uploading stays available. */
    skipped: boolean;
    onSkip: () => void;
}

const LogoSetup: FC<LogoSetupProps> = ({ logoUrl, uploading, disabled, onFileSelected, onFileRejected, skipped, onSkip }) => {
    // Reset after each pick so choosing the same file again still fires onChange.
    const resetRef = useRef<() => void>(null);
    // Phones can't drag files, so they're just asked to add one.
    const isPhone = useMediaQuery('(max-width: 48em)');
    const title = logoUrl ? 'Your logo in ServCraft' : isPhone ? 'Add your logo' : 'Drag and drop your logo here';
    return (
        <Paper radius={4} className={styles.task}>
            <Group gap={24}>
                <Group wrap="nowrap" gap="md">
                    <Dropzone
                        className={styles.dropzone}
                        // Inline so it beats Dropzone's own padding; the logo then fills the box.
                        p={0}
                        onDrop={files => { if (files[0]) onFileSelected(files[0]); }}
                        onReject={rejections => onFileRejected(describeLogoRejection(rejections), logoRejectionReason(rejections))}
                        maxSize={MAX_LOGO_BYTES}
                        accept={ACCEPTED_IMAGE_TYPES}
                        multiple={false}
                        disabled={disabled || uploading}
                        loading={false}
                        aria-label="Drop your logo here"
                        data-testid="onboarding-logo-dropzone"
                        id="onboarding-logo-dropzone"
                    >
                        <Box className={styles.preview}>
                            {uploading ? (
                                <Loader size="sm" color="scBlue" />
                            ) : logoUrl ? (
                                <Image src={logoUrl} alt="Company logo" w="100%" h="100%" fit="contain" />
                            ) : (
                                <IconPhoto size={40} stroke={1.5} color="var(--mantine-color-scBlue-6)" />
                            )}
                            {/* While a file is dragged over: covers the logo too, so the box always answers. */}
                            <Dropzone.Accept>
                                <div className={`${styles.dragState} ${styles.dragAccept}`} data-testid="onboarding-logo-drop-accept">
                                    <IconUpload size={20} />
                                    <span>Drop</span>
                                </div>
                            </Dropzone.Accept>
                            <Dropzone.Reject>
                                <div className={`${styles.dragState} ${styles.dragReject}`} data-testid="onboarding-logo-drop-reject">
                                    <IconX size={20} />
                                    <span>Not an image</span>
                                </div>
                            </Dropzone.Reject>
                        </Box>
                    </Dropzone>
                    <Stack gap={1}>
                        <Text className={styles.title}>{title}</Text>
                        <Text className={styles.text}>This will show on your quotes, invoices and job cards.</Text>
                    </Stack>
                </Group>
                <Group gap={16}>
                    <FileButton
                        resetRef={resetRef}
                        onChange={file => { if (file) onFileSelected(file); resetRef.current?.(); }}
                        accept={ACCEPTED_IMAGE_TYPES.join(',')}
                        disabled={disabled || uploading}
                        inputProps={{ 'data-testid': 'onboarding-logo-input' } as any}
                    >
                        {props => (logoUrl ? (
                            <SecondaryButton {...props} id="onboarding-change-logo" loading={uploading} disabled={disabled}>Change logo</SecondaryButton>
                        ) : (
                            <PrimaryButton {...props} id="onboarding-upload-logo" loading={uploading} disabled={disabled} leftSection={<IconCirclePlus size={20} />} className={styles.button}>
                                Upload logo
                            </PrimaryButton>
                        ))}
                    </FileButton>
                    {!logoUrl && !skipped && (
                        <SecondaryButton id="onboarding-no-logo" onClick={onSkip} disabled={disabled || uploading} className={styles.button}>I don't have one</SecondaryButton>
                    )}
                </Group>
            </Group>
        </Paper>
    );
};

export default LogoSetup;
